export interface DonationDonorRecord {
  id: number;
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string | number;
  message: string;
  organization: string;
  pan_number: string;
  created_at?: string | null;
  updated_at?: string | null;
  mode: string;
  address_line1?: string;
  address_line2?: string;
  state?: string | number;
  state_id?: string | number;
  state_name?: string | null;
  district?: string | number;
  district_id?: string | number;
  district_name?: string | null;
  city?: string | number;
  city_id?: string | number;
  city_name?: string | null;
  area?: string | number;
  area_id?: string | number;
  area_name?: string | null;
  pincode?: string;
  payment_mode: string;
  by_cheque_no?: string;
  is_head?: boolean;
  is_already_head?: boolean;
  head_phone?: string;
  children?: DonationDonorRecord[];
  relationship?: number;
  relationship_name?: string;
  csr_eligibility?: boolean | null;
}

/** Child entry in PATCH /donation-master/{parentId}/ */
export interface DonationDonorChildPayload {
  name: string;
  phone: string;
  relationship?: number;
}

export function buildDonationDonorChildPayload(
  child: DonationDonorRecord
): DonationDonorChildPayload {
  const payload: DonationDonorChildPayload = {
    name: child.name?.trim() ?? "",
    phone: child.phone?.trim() ?? "",
  };
  if (
    child.relationship !== null &&
    child.relationship !== undefined &&
    Number.isFinite(Number(child.relationship))
  ) {
    payload.relationship = Number(child.relationship);
  }
  return payload;
}

export interface DonationDonorPayload extends Record<string, unknown> {
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string | number;
  message: string;
  organization: string;
  pan_number: string;
  mode: string;
  address_line1?: string;
  address_line2?: string;
  state?: string | number;
  district?: string | number;
  city?: string | number;
  area?: string | number;
  pincode?: string;
  payment_mode: string;
  by_cheque_no?: string;
  is_head: boolean;
  is_already_head: boolean;
  head_phone?: string;
  wants_80g_receipt?: boolean;
  keep_anonymous_on_donor_wall?: boolean;
  csr_eligibility?: boolean;
}

export interface DonationDonorPatchPayload
  extends Partial<DonationDonorPayload> {
  children?: DonationDonorChildPayload[];
}

/** POST /jivandhara/donations/add/ */
export interface AddDonationPayload {
  scheme_id?: number;
  purpose_of_donation?: string;
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  mode: string;
  payment_mode: string;
  by_cheque_no?: string;
  message?: string;
  district: number;
  city: number;
  area: number;
  pincode: string;
}

export const PURPOSE_OF_DONATION_OPTIONS = [
  { value: "Gaushala", label: "Gaushala" },
  { value: "Hospital", label: "Hospital" },
  { value: "Ashram", label: "Ashram" },
] as const;

export const PAYMENT_MODE_OPTIONS = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
] as const;

export function formatDonationAmount(value: string | number): string {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return "0.00";
  }
  return num.toFixed(2);
}

export function buildDonationDonorPayload(
  payload: Record<string, unknown>
): DonationDonorPayload {
  const name = String(payload.name ?? "").trim();
  if (!name) {
    throw new Error("Name is required.");
  }
  const email = String(payload.email ?? "").trim();
  if (!email) {
    throw new Error("Email is required.");
  }
  const phone = String(payload.phone ?? "").trim();
  if (!phone) {
    throw new Error("Phone is required.");
  }
  const donationType = String(payload.donation_type ?? "").trim();
  if (!donationType) {
    throw new Error("Donation type is required.");
  }
  const amount = formatDonationAmount(String(payload.amount ?? ""));
  if (Number(amount) <= 0) {
    throw new Error("Valid amount is required.");
  }
  const paymentMode = String(payload.payment_mode ?? "")
    .trim()
    .toLowerCase();
  if (!paymentMode) {
    throw new Error("Payment mode is required.");
  }
  const panNumber = String(payload.pan_number ?? "")
    .trim()
    .toUpperCase();
  if (!panNumber) {
    throw new Error("PAN number is required.");
  }
  const addressLine1 = String(payload.address_line1 ?? "").trim();
  if (!addressLine1) {
    throw new Error("Address line 1 is required.");
  }
  if (!payload.district) {
    throw new Error("District is required.");
  }
  if (!payload.city) {
    throw new Error("City is required.");
  }
  if (!payload.area) {
    throw new Error("Area is required.");
  }
  const pincode = String(payload.pincode ?? "").trim();
  if (!/^[0-9]{6}$/.test(pincode)) {
    throw new Error("Valid 6-digit pincode is required.");
  }
  const isAlreadyHead = Boolean(payload.is_already_head);
  const headPhone = String(payload.head_phone ?? "").trim();
  if (isAlreadyHead && !/^[0-9]{10}$/.test(headPhone)) {
    throw new Error("Valid 10-digit head mobile number is required.");
  }

  const result: DonationDonorPayload = {
    name,
    email,
    phone,
    organization: String(payload.organization ?? "").trim(),
    donation_type: donationType,
    amount,
    mode: "offline",
    payment_mode: paymentMode,
    pan_number: panNumber,
    message: String(payload.message ?? "").trim(),
    address_line1: addressLine1,
    district: Number(payload.district),
    city: Number(payload.city),
    area: Number(payload.area),
    pincode,
    is_head: !isAlreadyHead,
    is_already_head: isAlreadyHead,
  };

  const chequeNo = String(payload.by_cheque_no ?? "").trim();
  if (chequeNo) {
    result.by_cheque_no = chequeNo;
  }
  if (isAlreadyHead) {
    result.head_phone = headPhone;
  }

  return result;
}

export function mapDonationDonorToForm(
  record: DonationDonorRecord
): Record<string, unknown> {
  return {
    ...record,
    district: String(record.district_id ?? record.district ?? ""),
    city: String(record.city_id ?? record.city ?? ""),
    area: String(record.area_id ?? record.area ?? ""),
    payment_mode: record.payment_mode?.toLowerCase() ?? "",
    is_head: record.is_already_head ? false : (record.is_head ?? true),
    is_already_head: Boolean(record.is_already_head),
    head_phone: record.head_phone ?? "",
    amount:
      record.amount !== null && record.amount !== undefined
        ? String(record.amount)
        : "",
  };
}
